"use client";

import type { DashboardWidget as DashboardWidgetDefinition } from "@mlx/contracts";
import { useState, type ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { cn } from "@/lib/utils";
import {
  additiveAggregate,
  compactNumber,
  displayUnitLabel,
  metricPeriodDelta,
  type ChartPoint,
  type MetricDisplayKind,
} from "./record-dashboard-model";
import {
  foldSegments,
  type GroupedBarData,
  type StackedBarData,
} from "./record-dashboard-segments";
import {
  compactCategoryLabel,
  DashboardCategoryChart,
  DashboardRadialChart,
} from "./record-dashboard-category-charts";
import {
  axisTickKind,
  DashboardSeriesChart,
  MetricSparkline,
  positiveTopDomain,
} from "./record-dashboard-series-chart";
import {
  DASHBOARD_CHART_FRAME,
  DashboardChart,
  DashboardLegend,
  DashboardTooltip,
  DashboardWidget,
  EmptyChart,
  dashboardSeriesColor,
} from "./record-dashboard-shell";

/*
 * The saved widgets' bodies. Each reads only its title, aggregate and variant
 * from the saved widget, so a fixture passes those with its points.
 */
type SavedChartWidget<
  Type extends DashboardWidgetDefinition["type"],
  Keys extends keyof Extract<DashboardWidgetDefinition, { type: Type }> = never,
> = Pick<Extract<DashboardWidgetDefinition, { type: Type }>, "title" | Keys> & {
  metric: { aggregate: string };
};

/** A metric tile: the value and its change on the prior period, what it measures, and its monthly line. */
export function MetricWidget({
  widget,
  data,
  displayKind,
  fieldLabel,
  description,
  meta,
  actions,
}: {
  widget: SavedChartWidget<"metric">;
  /** The computed value; none shows a dash. */
  data: { value: number; previous?: number; points?: ChartPoint[] } | null;
  displayKind: MetricDisplayKind;
  /** The measured field's label; a count of records has none. */
  fieldLabel?: string;
  description?: string;
  meta?: string;
  actions?: ReactNode;
}) {
  const delta = data ? metricPeriodDelta(data.value, data.previous) : null;
  const sparkline =
    data?.points && data.points.length >= 2 ? data.points : null;
  return (
    <DashboardWidget
      title={widget.title}
      description={description}
      meta={meta}
      actions={actions}
      className="h-full"
    >
      <div className="flex flex-1 flex-col justify-center gap-2 overflow-hidden px-0.5">
        <div className="flex min-w-0 items-baseline gap-2">
          <div className="mlx-dashboard-metric-value min-w-0 truncate font-semibold tabular-nums text-foreground">
            {data ? compactNumber(data.value, displayKind) : "-"}
          </div>
          {delta !== null ? (
            <span
              className={cn(
                "shrink-0 rounded-full px-1.5 py-0.5 text-2xs font-medium tabular-nums",
                delta >= 0
                  ? "bg-success/12 text-success"
                  : "bg-destructive/10 text-destructive",
              )}
              title="vs prior period"
            >
              {`${delta >= 0 ? "+" : ""}${Math.round(delta * 100)}%`}
            </span>
          ) : null}
        </div>
        <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
          <span className="truncate">{fieldLabel ?? "Records"}</span>
          <span
            className="size-1 shrink-0 rounded-full bg-border"
            aria-hidden
          />
          <span className="shrink-0 capitalize">
            {widget.metric.aggregate}
          </span>
        </div>
        {sparkline ? (
          <MetricSparkline points={sparkline} displayKind={displayKind} />
        ) : null}
      </div>
    </DashboardWidget>
  );
}

export function SeriesWidget({
  widget,
  data,
  displayKind,
  description,
  meta,
  actions,
  seriesName,
}: {
  widget: SavedChartWidget<"time_series">;
  data: ChartPoint[];
  displayKind: MetricDisplayKind;
  description?: string;
  meta?: string;
  actions?: ReactNode;
  seriesName?: string;
}) {
  return (
    <DashboardWidget
      title={widget.title}
      description={description}
      unit={displayUnitLabel(displayKind)}
      meta={meta}
      actions={actions}
      className="h-full"
    >
      {data.length ? (
        <DashboardSeriesChart
          title={widget.title}
          data={data}
          displayKind={displayKind}
          seriesName={seriesName ?? "Records"}
          integerTicks={widget.metric.aggregate === "count"}
        />
      ) : (
        <EmptyChart title="No dated records">
          No records match this widget&apos;s date field and current filters.
        </EmptyChart>
      )}
    </DashboardWidget>
  );
}

export function BreakdownWidget({
  widget,
  data,
  displayKind,
  description,
  meta,
  actions,
  seriesName,
  onPointSelect,
}: {
  widget: SavedChartWidget<"breakdown", "variant">;
  data: ChartPoint[];
  displayKind: MetricDisplayKind;
  description?: string;
  meta?: string;
  actions?: ReactNode;
  seriesName?: string;
  onPointSelect?: (label: string) => void;
}) {
  const additive = additiveAggregate(widget.metric.aggregate);
  return (
    <DashboardWidget
      title={widget.title}
      description={description}
      unit={displayUnitLabel(displayKind)}
      meta={meta}
      actions={actions}
      className="h-full"
    >
      {data.length ? (
        widget.variant === "pie" ? (
          <DashboardRadialChart
            title={widget.title}
            data={data}
            displayKind={displayKind}
            variant="pie"
            additive={additive}
            onPointSelect={onPointSelect}
          />
        ) : (
          <DashboardCategoryChart
            title={widget.title}
            data={data}
            displayKind={displayKind}
            seriesName={seriesName}
            additive={additive}
            integerTicks={widget.metric.aggregate === "count"}
            onPointSelect={onPointSelect}
          />
        )
      ) : (
        <EmptyChart title="No breakdown values">
          No category values match the current dashboard filters.
        </EmptyChart>
      )}
    </DashboardWidget>
  );
}

export function BarWidget({
  widget,
  data,
  displayKind,
  description,
  meta,
  actions,
  seriesName,
  onPointSelect,
}: {
  widget: SavedChartWidget<"bar", "variant">;
  data: ChartPoint[];
  displayKind: MetricDisplayKind;
  description?: string;
  meta?: string;
  actions?: ReactNode;
  seriesName?: string;
  onPointSelect?: (label: string) => void;
}) {
  const additive = additiveAggregate(widget.metric.aggregate);
  return (
    <DashboardWidget
      title={widget.title}
      description={description}
      unit={displayUnitLabel(displayKind)}
      meta={meta}
      actions={actions}
      className="h-full"
    >
      {data.length ? (
        widget.variant === "pie" || widget.variant === "donut" ? (
          <DashboardRadialChart
            title={widget.title}
            data={data}
            displayKind={displayKind}
            variant={widget.variant}
            additive={additive}
            onPointSelect={onPointSelect}
          />
        ) : (
          <DashboardCategoryChart
            title={widget.title}
            data={data}
            displayKind={displayKind}
            seriesName={seriesName}
            additive={additive}
            integerTicks={widget.metric.aggregate === "count"}
            onPointSelect={onPointSelect}
          />
        )
      ) : (
        <EmptyChart title="No comparison values">
          No category values match the current dashboard filters.
        </EmptyChart>
      )}
    </DashboardWidget>
  );
}

export function StackedBarWidget({
  widget,
  data,
  displayKind,
  description,
  meta,
  actions,
}: {
  widget: SavedChartWidget<"stacked_bar">;
  data: StackedBarData;
  displayKind: MetricDisplayKind;
  description?: string;
  meta?: string;
  actions?: ReactNode;
}) {
  // Folded totals stay exact for additive metrics; stacked averages keep
  // every segment (overflow-coloured) rather than a misleading "Other" sum.
  const chart = additiveAggregate(widget.metric.aggregate)
    ? foldSegments(data)
    : data;
  const [hiddenSegments, setHiddenSegments] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const toggleSegment = (key: string) =>
    setHiddenSegments((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const lastSegmentKey = chart.segments
    .filter((segment) => !hiddenSegments.has(segment.key))
    .at(-1)?.key;

  return (
    <DashboardWidget
      title={widget.title}
      description={description}
      unit={displayUnitLabel(displayKind)}
      meta={meta}
      actions={actions}
      className="h-full"
    >
      {chart.points.length && chart.segments.length ? (
        <>
          <DashboardChart
            label={widget.title}
            summary={`${widget.title}. ${chart.points.length} time buckets across ${chart.segments.length} series.`}
          >
            <ResponsiveContainer
              width="100%"
              height="100%"
              initialDimension={{ width: 480, height: 220 }}
            >
              <BarChart
                accessibilityLayer
                data={chart.points}
                margin={DASHBOARD_CHART_FRAME.labelledMargin}
                barCategoryGap="22%"
              >
                <CartesianGrid
                  stroke="hsl(var(--border))"
                  strokeOpacity={DASHBOARD_CHART_FRAME.gridOpacity}
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={9}
                  minTickGap={14}
                />
                <YAxis
                  domain={positiveTopDomain(1.15)}
                  allowDecimals={widget.metric.aggregate !== "count"}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={7}
                  width={DASHBOARD_CHART_FRAME.yAxisWidth}
                  tickFormatter={(value) =>
                    compactNumber(Number(value), axisTickKind(displayKind))
                  }
                />
                <Tooltip
                  cursor={{ fill: "hsl(var(--muted))", fillOpacity: 0.35 }}
                  content={
                    <DashboardTooltip
                      displayKind={displayKind}
                      includeStackSummary={hiddenSegments.size === 0}
                    />
                  }
                />
                {chart.segments.map((segment, index) => (
                  <Bar
                    key={segment.key}
                    dataKey={segment.key}
                    name={segment.label}
                    stackId="total"
                    hide={hiddenSegments.has(segment.key)}
                    fill={dashboardSeriesColor(index)}
                    radius={
                      segment.key === lastSegmentKey
                        ? [4, 4, 0, 0]
                        : [0, 0, 0, 0]
                    }
                    maxBarSize={DASHBOARD_CHART_FRAME.stackedMaxBarSize}
                    isAnimationActive={false}
                  >
                    {segment.key === lastSegmentKey &&
                    hiddenSegments.size === 0 &&
                    chart.points.length <= 8 ? (
                      <LabelList
                        dataKey="total"
                        position="top"
                        formatter={(value) =>
                          compactNumber(Number(value ?? 0), displayKind)
                        }
                        className="fill-foreground text-3xs font-medium"
                      />
                    ) : null}
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          </DashboardChart>
          <DashboardLegend
            items={segmentLegend(chart.segments)}
            hiddenKeys={hiddenSegments}
            onToggle={toggleSegment}
          />
        </>
      ) : (
        <EmptyChart title="No stacked values">
          No dated segment values match the current dashboard filters.
        </EmptyChart>
      )}
    </DashboardWidget>
  );
}

export function GroupedBarWidget({
  widget,
  data,
  displayKind,
  description,
  meta,
  actions,
}: {
  widget: SavedChartWidget<"grouped_bar", "variant">;
  data: GroupedBarData;
  displayKind: MetricDisplayKind;
  description?: string;
  meta?: string;
  actions?: ReactNode;
}) {
  const chart = additiveAggregate(widget.metric.aggregate)
    ? foldSegments(data)
    : data;
  const [hiddenSegments, setHiddenSegments] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const toggleSegment = (key: string) =>
    setHiddenSegments((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  // The saved definition chooses grouped or stacked; the trace echoes the
  // variant, so the rendering must honour it rather than always grouping.
  const stacked = widget.variant === "stacked";
  const lastSegmentKey = chart.segments
    .filter((segment) => !hiddenSegments.has(segment.key))
    .at(-1)?.key;
  return (
    <DashboardWidget
      title={widget.title}
      description={description}
      unit={displayUnitLabel(displayKind)}
      meta={meta}
      actions={actions}
      className="h-full"
    >
      {chart.points.length && chart.segments.length ? (
        <>
          <DashboardChart
            label={widget.title}
            summary={`${widget.title}. ${chart.points.length} categories across ${chart.segments.length} ${stacked ? "stacked" : "grouped"} series.`}
          >
            <ResponsiveContainer
              width="100%"
              height="100%"
              initialDimension={{ width: 480, height: 220 }}
            >
              <BarChart
                accessibilityLayer
                data={chart.points}
                margin={DASHBOARD_CHART_FRAME.margin}
                barGap={2}
                barCategoryGap={stacked ? "22%" : "24%"}
              >
                <CartesianGrid
                  stroke="hsl(var(--border))"
                  strokeOpacity={DASHBOARD_CHART_FRAME.gridOpacity}
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={9}
                  minTickGap={14}
                  tickFormatter={compactCategoryLabel}
                />
                <YAxis
                  domain={positiveTopDomain(1.15)}
                  allowDecimals={widget.metric.aggregate !== "count"}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={7}
                  width={DASHBOARD_CHART_FRAME.yAxisWidth}
                  tickFormatter={(value) =>
                    compactNumber(Number(value), axisTickKind(displayKind))
                  }
                />
                <Tooltip
                  cursor={{ fill: "hsl(var(--muted))", fillOpacity: 0.35 }}
                  content={
                    <DashboardTooltip
                      displayKind={displayKind}
                      includeStackSummary={hiddenSegments.size === 0}
                    />
                  }
                />
                {chart.segments.map((segment, index) => (
                  <Bar
                    key={segment.key}
                    dataKey={segment.key}
                    name={segment.label}
                    stackId={stacked ? "total" : undefined}
                    hide={hiddenSegments.has(segment.key)}
                    fill={dashboardSeriesColor(index)}
                    radius={
                      stacked
                        ? segment.key === lastSegmentKey
                          ? [4, 4, 0, 0]
                          : [0, 0, 0, 0]
                        : [4, 4, 1, 1]
                    }
                    maxBarSize={
                      stacked
                        ? DASHBOARD_CHART_FRAME.stackedMaxBarSize
                        : DASHBOARD_CHART_FRAME.groupedMaxBarSize
                    }
                    isAnimationActive={false}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </DashboardChart>
          <DashboardLegend
            items={segmentLegend(chart.segments)}
            hiddenKeys={hiddenSegments}
            onToggle={toggleSegment}
          />
        </>
      ) : (
        <EmptyChart title="No grouped values">
          No grouped segment values match the current dashboard filters.
        </EmptyChart>
      )}
    </DashboardWidget>
  );
}

function segmentLegend(segments: Array<{ key: string; label: string }>) {
  return segments.map((segment, index) => ({
    key: segment.key,
    label: segment.label,
    color: dashboardSeriesColor(index),
  }));
}
